/**
 * Modal Component
 * 
 * Contact form modal with validation and EmailJS integration.
 */

import { useEffect, useRef, useCallback } from 'react'
import { useModal } from '../context/ModalContext'
import ContactForm from './ContactForm'
import '../styles/Modal.css'

function Modal() {
  const { isOpen, selectedPlan, closeModal } = useModal()
  const modalRef = useRef(null)
  const firstFocusableRef = useRef(null)

  /**
   * Handle escape key to close modal
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeModal()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, closeModal])

  /**
   * Focus first input when modal opens
   */
  useEffect(() => {
    if (isOpen && firstFocusableRef.current) {
      setTimeout(() => {
        firstFocusableRef.current.focus()
      }, 300)
    }
  }, [isOpen])

  /**
   * Handle click outside modal to close
   */
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      closeModal()
    }
  }, [closeModal])

  /**
   * Trap focus inside modal
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Tab') return

    const modal = modalRef.current
    if (!modal) return

    const focusableElements = modal.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault()
        lastElement.focus()
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault()
        firstElement.focus()
      }
    }
  }, [])

  return (
    <div 
      className={`modal-overlay ${isOpen ? 'active' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modalTitle"
      aria-hidden={!isOpen}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
    >
      <div className="modal" ref={modalRef} role="document">
        {/* Modal Header */}
        <div className="modal-header">
          <h2 id="modalTitle" className="modal-title">
            <i className="fas fa-file-alt" aria-hidden="true"></i>
            Kreditanfrage
          </h2>
          <button 
            className="modal-close"
            onClick={closeModal}
            aria-label="Modal schließen"
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>

        {/* Selected Plan Display */}
        <div className="selected-plan">
          <div className="selected-plan-info">
            <span className="plan-type">{selectedPlan.type}</span>
            <span className="plan-price">{selectedPlan.price}</span>
          </div>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          <ContactForm 
            selectedPlan={selectedPlan}
            firstFocusableRef={firstFocusableRef}
          />
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <p>
            <i className="fas fa-lock" aria-hidden="true"></i>
            Ihre Daten werden sicher und vertraulich behandelt.
          </p>
        </div>
      </div>
    </div>
  )
}

export default Modal
